import { Component, EventEmitter, Output, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastService } from '@peca/commons';
import { ModalComponent } from '@peca/design-system';

import { RegionsService } from '../../../services/regions.service';
import { CreateRegionRequest, Region } from '../../../types/region.type';

type RegionModalMode = 'create' | 'details';

@Component({
  selector: 'peca-region-modal-form',
  templateUrl: './region-modal-form.component.html',
  styleUrls: ['./region-modal-form.component.scss'],
  standalone: false,
})
export class RegionModalFormComponent {
  @ViewChild('modal') modal!: ModalComponent;
  @Output() created = new EventEmitter<void>();

  private readonly regionsService = inject(RegionsService);
  private readonly toast = inject(ToastService);

  mode: RegionModalMode = 'create';
  loading = false;
  audit?: Region['audit'];

  readonly form = new FormGroup({
    id: new FormControl<number | null>(null),
    description: new FormControl('', [Validators.required]),
    originZipCode: new FormControl('', [Validators.required]),
    destinationZipCode: new FormControl('', [Validators.required]),
  });

  get title() {
    return this.mode === 'create' ? 'Nova região' : 'Detalhes da região';
  }

  openCreate() {
    this.mode = 'create';
    this.audit = undefined;
    this.form.enable();
    this.form.reset({
      id: null,
      description: '',
      originZipCode: '',
      destinationZipCode: '',
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.modal.open();
  }

  openDetails(id: number) {
    this.mode = 'details';
    this.loading = true;
    this.form.disable();
    this.modal.open();

    this.regionsService.getById(id).subscribe({
      next: (region) => {
        this.form.patchValue({
          id: region.id,
          description: region.name,
          originZipCode: region.originZipCode,
          destinationZipCode: region.destinationZipCode,
        });
        this.audit = region.audit;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toast.failure('Não foi possível carregar os detalhes da região.');
        this.close();
      },
    });
  }

  close() {
    this.modal.close();
  }

  onDismiss() {
    this.audit = undefined;
    this.form.reset({
      id: null,
      description: '',
      originZipCode: '',
      destinationZipCode: '',
    });
    this.form.enable();
    this.loading = false;
  }

  onClickSave() {
    if (this.form.invalid || this.loading) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.form.disable();

    this.regionsService.create(this.payload).subscribe({
      next: () => {
        this.loading = false;
        this.toast.success('Região cadastrada com sucesso.');
        this.close();
        this.created.emit();
      },
      error: (error: any) => {
        this.loading = false;
        this.form.enable();

        if (error?.status === 409) {
          this.toast.failure('Essa região já está cadastrada.');
          return;
        }

        this.toast.failure('Falha ao cadastrar região.');
      },
    });
  }

  showFieldError(fieldName: 'description' | 'originZipCode' | 'destinationZipCode') {
    const field = this.form.get(fieldName);
    return !!field && field.invalid && (field.dirty || field.touched);
  }

  get payload(): CreateRegionRequest {
    const raw = this.form.getRawValue();

    return {
      name: (raw.description ?? '').trim(),
      originZipCode: this.normalizeZipCode(raw.originZipCode),
      destinationZipCode: this.normalizeZipCode(raw.destinationZipCode),
    };
  }

  private normalizeZipCode(value: string | null): string {
    return (value ?? '').replace(/\D/g, '');
  }
}
